// Firebase client for the portal.
//
// The SDK owns the session: it persists the refresh token, refreshes the ID
// token before it expires, and survives a reload. We never store a Firebase
// token ourselves — copying it into localStorage would put a live credential
// somewhere it does not need to be.
//
// Firebase is optional. With no config the portal falls back to the legacy
// magic-link JWT, so a build without these vars still works.

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  onIdTokenChanged,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const isConfigured = Boolean(config.apiKey && config.projectId && config.appId);

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

if (isConfigured) {
  app = initializeApp(config);
  auth = getAuth(app);
}

export const firebaseEnabled = isConfigured;

export function getFirebaseAuth(): Auth | null {
  return auth;
}

/** Exchange the backend's custom token for a Firebase session. */
export async function signInWithPortalToken(customToken: string): Promise<User> {
  if (!auth) throw new Error('Firebase is not configured');
  const credential = await signInWithCustomToken(auth, customToken);
  return credential.user;
}

/**
 * Email + password — the App Review demo account's path, mirrored from the
 * mobile app (see packages/mobile/services/firebase.ts for why review
 * credentials must be native). Clients have no passwords; the provider serves
 * exactly one console-created user.
 */
export async function signInWithEmail(email: string, password: string): Promise<User> {
  if (!auth) throw new Error('Firebase is not configured');
  const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
  return credential.user;
}

/**
 * Current ID token, refreshed by the SDK when it is close to expiring. Returns
 * null when nobody is signed in, which is the signal to fall back to the
 * legacy token.
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = auth?.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken(forceRefresh);
  } catch {
    // A revoked or disabled account fails here; treat it as signed out and let
    // the caller fall through to the 401 path.
    return null;
  }
}

/**
 * Popup where it works, redirect where it does not.
 *
 * Popups are blocked inside in-app browsers — WhatsApp's above all, which is
 * how most of these customers arrive — and Safari blocks them unless the click
 * is trusted. Falling back to redirect keeps sign-in working there rather than
 * failing with a message nobody can act on.
 */
async function signInWithProvider(provider: GoogleAuthProvider | OAuthProvider): Promise<User> {
  if (!auth) throw new Error('Firebase is not configured');

  try {
    const credential = await signInWithPopup(auth, provider);
    return credential.user;
  } catch (error) {
    const code = (error as { code?: string }).code;
    const popupBlocked =
      code === 'auth/popup-blocked' ||
      code === 'auth/operation-not-supported-in-this-environment' ||
      code === 'auth/cancelled-popup-request';

    if (!popupBlocked) throw error;

    // Navigates away; the result is picked up on return by onIdTokenChanged.
    await signInWithRedirect(auth, provider);
    throw new Error('redirecting');
  }
}

export async function signInWithGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider();
  // Always ask which account — these customers routinely have a personal and a
  // work address, and only one of them is on their supplier's contact record.
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithProvider(provider);
}

export async function signInWithApple(): Promise<User> {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  return signInWithProvider(provider);
}

export async function firebaseSignOut(): Promise<void> {
  if (auth) await signOut(auth);
}

/**
 * Fires on sign-in, sign-out, and every token refresh. Used to notice a session
 * ending in another tab, since Firebase persists to shared browser storage.
 */
export function watchIdToken(callback: (user: User | null) => void): () => void {
  if (!auth) return () => undefined;
  return onIdTokenChanged(auth, callback);
}
